import { Injectable, Injector, NgZone, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AutoLogoutService } from './auto-logout.service';

/**
 * sessionStorage key holding the reason a tab was logged out by *another* tab,
 * so the sign-in page can explain why the user suddenly landed there.
 */
export const SESSION_LOGOUT_NOTICE_KEY = 'sessionLogoutNotice';

const LOGOUT_CHANNEL = 'hippo-session';
const LOGOUT_MESSAGE = 'logout';

/**
 * localStorage key used as a second signal path: `storage` events fire in every
 * other tab of the same origin, which also covers browsers without
 * BroadcastChannel support.
 */
const LOGOUT_SIGNAL_KEY = 'hippoLogoutSignal';

interface LogoutSignal {
    type: string;
    /** The uid whose PHP session was ended — never empty. */
    uid: string;
    /** Only there to make every localStorage write a value change. */
    nonce?: string;
}

/**
 * Keeps the login state in sync across the browser tabs that share one and the
 * same session.
 *
 * The logged-in user lives in `sessionStorage`, which is per-tab — but a
 * duplicated tab inherits a copy of it, so two tabs can hold the very same
 * `uid`. Signing out in one of them ends that uid's PHP session while the
 * other one keeps navigating on a session that is already gone (HIP-40). This
 * service broadcasts the sign-out so those tabs drop their session immediately
 * instead of on their next failing request.
 *
 * The signal carries the uid that was logged out and a receiving tab only acts
 * on it when it is its *own* uid: the PHP session data is stored per uid
 * (`$_SESSION[uid]`), so a tab logged in as a different user keeps a perfectly
 * valid session and must not be signed out with it (HIP-62).
 */
@Injectable({ providedIn: 'root' })
export class SessionSyncService implements OnDestroy {
    private readonly router = inject(Router);
    private readonly zone = inject(NgZone);
    private readonly oidc = inject(OidcSecurityService);
    private readonly injector = inject(Injector);

    private channel: BroadcastChannel | null = null;
    private storageListener: ((event: StorageEvent) => void) | null = null;
    private listening = false;

    /**
     * Starts listening for sign-outs from other tabs. Called once on app start;
     * repeated calls are ignored.
     */
    start(): void {
        if (this.listening || typeof window === 'undefined') {
            return;
        }
        this.listening = true;

        if (typeof BroadcastChannel !== 'undefined') {
            try {
                this.channel = new BroadcastChannel(LOGOUT_CHANNEL);
                this.channel.onmessage = event => {
                    this.onRemoteLogout(event?.data);
                };
            } catch {}
        }

        this.storageListener = event => {
            if (event.key !== LOGOUT_SIGNAL_KEY || !event.newValue) {
                return;
            }
            try {
                this.onRemoteLogout(JSON.parse(event.newValue));
            } catch {}
        };
        window.addEventListener('storage', this.storageListener);
    }

    ngOnDestroy(): void {
        if (this.storageListener) {
            window.removeEventListener('storage', this.storageListener);
            this.storageListener = null;
        }
        try {
            this.channel?.close();
        } catch {}
        this.channel = null;
        this.listening = false;
    }

    /**
     * Signals the other tabs running on this tab's session that it has ended.
     * Must be called *before* the sign-out clears the session storage, and
     * before a logout redirect navigates this tab away.
     */
    broadcastLogout(): void {
        if (typeof window === 'undefined') {
            return;
        }
        const uid = this.currentUid();
        // Without a uid there is no session to end anywhere else — staying
        // quiet keeps other users' tabs untouched (HIP-62).
        if (!uid) {
            return;
        }
        const signal: LogoutSignal = { type: LOGOUT_MESSAGE, uid };
        try {
            this.channel?.postMessage(signal);
        } catch {}
        try {
            // The value has to change on every logout, otherwise the `storage`
            // event does not fire in the other tabs.
            window.localStorage.setItem(
                LOGOUT_SIGNAL_KEY,
                JSON.stringify({
                    ...signal,
                    nonce: `${Date.now()}-${Math.random()}`,
                })
            );
        } catch {}
    }

    /**
     * Another tab signed out: end this tab's session too, but only when it is
     * the same session — a tab logged in as a different user keeps its own,
     * still valid PHP session (HIP-62). Both signal paths can arrive, so this
     * is a no-op once the local session is already gone.
     */
    private onRemoteLogout(signal: LogoutSignal | undefined): void {
        if (signal?.type !== LOGOUT_MESSAGE || !signal.uid) {
            return;
        }
        const uid = this.currentUid();
        if (!uid || uid !== signal.uid) {
            return;
        }
        this.zone.run(() => {
            // Lazy lookup: AutoLogoutService pulls in TranslocoService, whose
            // http loader ends up back at the auth services — injecting it in a
            // field would make that a circular dependency.
            try {
                this.injector.get(AutoLogoutService).clearTimer();
            } catch {}
            // Drop the OIDC token store as well, otherwise the memoized Cognito
            // handshake could re-exchange this tab's still-cached token and log
            // the user straight back in (same failure mode as HIP-33).
            try {
                this.oidc.logoffLocal();
            } catch {}
            try {
                sessionStorage.clear();
            } catch {}
            try {
                sessionStorage.setItem(
                    SESSION_LOGOUT_NOTICE_KEY,
                    this.logoutNotice()
                );
            } catch {}
            this.router.navigate(['/sign-in']);
        });
    }

    /**
     * The uid of the session this tab is logged in with, or null when the tab
     * holds no session.
     */
    private currentUid(): string | null {
        try {
            const user = sessionStorage.getItem('user');
            return (user ? JSON.parse(user)?.uid : null) || null;
        } catch {
            return null;
        }
    }

    private logoutNotice(): string {
        let lang = 'de';
        try {
            const transloco = this.injector.get(TranslocoService);
            lang = transloco.getActiveLang() || transloco.getDefaultLang();
        } catch {}
        return lang === 'en'
            ? 'You have been logged out in another tab.'
            : 'Sie wurden in einem anderen Tab abgemeldet.';
    }
}
